import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useInstallPrompt from '../../hooks/useInstallPrompt';
import { DEFAULT_THEME_COLOR, DEFAULT_GRADIENT } from '../../constants/theme';

const STORAGE_KEY = 'a2hs_dismissed_at';
const DISMISS_TTL = 7 * 24 * 60 * 60 * 1000;
const SHOW_DELAY = 3000;

const Step = ({ number, icon, text }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25 }}>
    <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: `${DEFAULT_THEME_COLOR}22`, border: `1px solid ${DEFAULT_THEME_COLOR}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Typography sx={{ color: DEFAULT_THEME_COLOR, fontSize: '0.75rem', fontWeight: 700 }}>{number}</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.88rem' }}>{text}</Typography>
    </Box>
  </Box>
);

const AddToHomeScreenBanner = () => {
  const { deferredPrompt, isIos, isInStandaloneMode, isInstalled, triggerInstall } = useInstallPrompt();
  const [visible, setVisible] = useState(false);
  const [iosDialogOpen, setIosDialogOpen] = useState(false);

  const canInstall = (isIos || !!deferredPrompt) && !isInstalled && !isInStandaloneMode;

  useEffect(() => {
    if (!canInstall) return;
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed && Date.now() - parseInt(dismissed) < DISMISS_TTL) return;
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY);
    return () => clearTimeout(timer);
  }, [canInstall]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setVisible(false);
  };

  const handleInstall = async () => {
    if (isIos) {
      setIosDialogOpen(true);
    } else {
      const accepted = await triggerInstall();
      if (accepted) setVisible(false);
    }
  };

  if (!visible) return null;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 72,
          left: 12,
          right: 12,
          zIndex: 1400,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg, #0d1b2a 0%, #0a1929 100%)',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          component="img"
          src="/icon-72x72.png"
          alt="Freakland"
          sx={{ width: 44, height: 44, borderRadius: 1.5, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.88rem', lineHeight: 1.2 }}>
            Freakland
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', mt: 0.25 }}>
            Add to your home screen
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          onClick={handleInstall}
          sx={{
            background: DEFAULT_GRADIENT,
            color: 'white',
            fontWeight: 700,
            fontSize: '0.78rem',
            px: 1.5,
            py: 0.75,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            borderRadius: 1.5,
          }}
        >
          Add
        </Button>
        <IconButton size="small" onClick={dismiss} sx={{ color: 'rgba(255,255,255,0.35)', flexShrink: 0, ml: -0.5 }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Dialog
        open={iosDialogOpen}
        onClose={() => { setIosDialogOpen(false); dismiss(); }}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #0d1b2a 0%, #091420 100%)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 3,
            maxWidth: 360,
            width: '100%',
            m: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pt: 2.5, pb: 0 }}>
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>Add to Home Screen</Typography>
          <IconButton size="small" onClick={() => { setIosDialogOpen(false); dismiss(); }} sx={{ color: 'rgba(255,255,255,0.5)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ px: 2.5, pt: 1.5, pb: 2.5 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', mb: 2 }}>
            Follow these steps in Safari to install Freakland on your home screen:
          </Typography>
          <Step number="1" icon={<IosShareIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 20, flexShrink: 0 }} />} text="Tap the Share button at the bottom of your browser" />
          <Step number="2" icon={<AddBoxOutlinedIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 20, flexShrink: 0 }} />} text='"Add to Home Screen" from the menu' />
          <Step number="3" icon={<CheckCircleIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 20, flexShrink: 0 }} />} text='Tap "Add" — Freakland will appear as an app icon' />
          <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, background: `${DEFAULT_THEME_COLOR}12`, border: `1px solid ${DEFAULT_THEME_COLOR}30` }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}>
              Works best in <strong style={{ color: 'white' }}>Safari</strong> on iPhone and iPad.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddToHomeScreenBanner;
